import { FaqItem } from '../../medleveSeo/types'

/**
 * Copy of the Medleve organic homepage.
 *
 * Unless stated otherwise every string is the EXACT wording of the SEO + GEO PRD (section numbers in
 * the comments). Do not paraphrase, extend or "improve" medical statements here: any change to a block
 * marked REQUIRES MEDICAL REVIEW / REQUIRES LEGAL REVIEW needs a new sign-off first.
 */

/**
 * PRD v4 §15: the title does not repeat the H1 and keeps "emagrecimento", "consulta médica online" and "Medleve".
 * The PRD gives the terms, not the sentence: this wording is editorial.
 */
export const HOME_TITLE = 'Consulta médica online para emagrecimento | Medleve'

/** PRD §35. */
export const HOME_META_DESCRIPTION =
  'Faça uma avaliação médica online para obesidade e sobrepeso. Atendimento por mensagem ou vídeo e tratamento individualizado pela Medleve.'

/**
 * PRD v4 §15–16 — hero. The H1 is the PRD's explicit exception to §8, §10, §12 and §17, valid for this H1
 * only; its asterisk points at `note`, which must stay visible right below the hero block and in the
 * server HTML.
 * REQUIRES MEDICAL REVIEW — treatment/prescription wording.
 */
export const HOME_HERO = {
  h1: 'Obtenha sua prescrição* agora e compre sua caneta de emagrecimento',
  note: '*Prescrição sujeita a avaliação médica e emitida apenas quando houver indicação clínica.',
  subheadline:
    'Faça uma avaliação médica online para obesidade e sobrepeso e descubra qual tratamento pode ser adequado ao seu caso.',
  complementary:
    'Atendimento por mensagem ou vídeo, acompanhamento médico e receita digital quando houver indicação clínica.',
  imageAlt: 'Médico de jaleco branco usando um smartphone no consultório'
} as const

/**
 * Availability badge of the current mobile hero, kept as-is.
 * REQUIRES LEGAL REVIEW — static "doctors online now" claim; confirm it holds at all hours or remove.
 */
export const HOME_HERO_AVAILABILITY_BADGE = 'Médicos online agora'

/**
 * Benefits (PRD §78 "Benefícios").
 * 1 and 4 are the wording of the current landing. 2 and 3 replace "Compre sua caneta online ou na
 * farmácia de sua escolha", "Receba sua recomendação digitalmente" and "Baixe sua receita assinada
 * digitalmente", which conflict with PRD §6 (no direct-sale wording) and §13 (no guaranteed prescription);
 * the replacements are fragments of the PRD §12 complementary line.
 * REQUIRES MEDICAL REVIEW.
 */
export const HOME_BENEFITS: readonly string[] = [
  'Acompanhamento médico 100% online',
  'Atendimento por mensagem ou vídeo',
  'Receita digital quando houver indicação clínica',
  'Sem filas e sem burocracia'
]

/**
 * Trust strip of the current landing ("Como funciona" marquee), wording unchanged.
 * REQUIRES LEGAL REVIEW — regulatory claims ("ANVISA regulamentado", "Telemedicina autorizada no Brasil").
 */
export const HOME_TRUST_STRIP: readonly string[] = [
  'Pagamento 100% seguro',
  'Seus dados estão protegidos (LGPD)',
  'ANVISA regulamentado',
  'Telemedicina autorizada no Brasil'
]

/**
 * PRD §14 — "Como funciona a Medleve?".
 * REQUIRES MEDICAL REVIEW.
 */
export const HOME_HOW_IT_WORKS = {
  title: 'Como funciona a Medleve?',
  imageAlt: 'Caneta aplicadora de medicamento injetável com seletor de dose',
  steps: [
    {
      title: 'Conte sobre sua saúde',
      description: 'Informe seu histórico, peso, medicamentos utilizados, tratamentos anteriores e objetivos.'
    },
    {
      title: 'Escolha seu atendimento',
      description: 'A consulta pode acontecer por mensagem ou vídeo.'
    },
    {
      title: 'Passe pela avaliação médica',
      description:
        'O médico analisa as informações, esclarece dúvidas e avalia as opções de tratamento adequadas ao caso.'
    },
    {
      title: 'Receita quando indicada',
      description: 'Caso exista indicação clínica, o médico poderá emitir uma receita digital.'
    },
    {
      title: 'Continue seu acompanhamento',
      description:
        'O acompanhamento permite avaliar evolução, tolerabilidade, efeitos adversos e necessidade de ajustes.'
    }
  ]
} as const

/**
 * PRD §15 + §7 — consultation modalities.
 * The "em poucos minutos" wording is used on purpose: the "menos de 5 minutos" variant is NOT
 * operationally validated and must not be published.
 * REQUIRES MEDICAL REVIEW · REQUIRES LEGAL REVIEW (availability claim).
 */
export const HOME_VIDEO_CONSULTATION = {
  title: 'Consulta médica por vídeo',
  body: 'Para quem prefere atendimento em tempo real, a Medleve também oferece consulta médica por vídeo, com profissionais disponíveis em poucos minutos.',
  modalities: [
    { title: 'Consulta por mensagem', description: 'Atendimento médico digital por mensagens.' },
    { title: 'Consulta por vídeo', description: 'Atendimento médico em tempo real por vídeo.' }
  ]
} as const

/**
 * PRD §16.
 * REQUIRES MEDICAL REVIEW.
 */
export const HOME_TREATMENTS = {
  title: 'Tratamentos para obesidade e sobrepeso',
  paragraphs: [
    'O tratamento da obesidade e do sobrepeso pode envolver mudanças no estilo de vida, acompanhamento profissional e, em alguns casos, medicamentos.',
    'Entre as opções avaliadas pelos médicos estão tratamentos relacionados às vias de GLP-1, como medicamentos à base de semaglutida ou tirzepatida.',
    'A escolha depende do histórico clínico, das contraindicações, da indicação de cada medicamento e da avaliação individual do profissional.'
  ]
} as const

/**
 * PRD §18.
 * REQUIRES MEDICAL REVIEW.
 */
export const HOME_GLP1 = {
  title: 'O que são medicamentos GLP-1?',
  paragraphs: [
    'GLP-1 é um hormônio produzido naturalmente pelo organismo e envolvido, entre outras funções, na regulação do apetite e da glicemia.',
    'Alguns medicamentos utilizados no tratamento de diabetes, obesidade ou sobrepeso atuam sobre essa via.',
    'A semaglutida atua como agonista do receptor de GLP-1. A tirzepatida atua sobre os receptores de GIP e GLP-1.',
    'Medicamentos diferentes podem ter indicações, doses, apresentações e perfis regulatórios diferentes.'
  ]
} as const

/**
 * PRD §17 — "Medicamentos e moléculas frequentemente pesquisados".
 * The brand → molecule pairs are NOT written here: they are read from the medleveSeo registry
 * (PRD §30/§31), so visible text, internal links and JSON-LD always agree.
 * - `intro` and the `groupLabel*` connectors are editorial glue written for this page (not PRD text);
 *   they only state that a brand is based on a molecule and that brand ≠ active ingredient (PRD §48).
 * - `clinicalNote` is PRD §4 and `pharmacyNote` is PRD §6, both verbatim.
 * REQUIRES MEDICAL REVIEW · REQUIRES LEGAL REVIEW (pharmacy intermediation wording).
 */
export const HOME_MEDICATIONS = {
  title: 'Medicamentos e moléculas frequentemente pesquisados',
  intro: 'Marca e princípio ativo não são a mesma coisa: cada medicamento abaixo aparece junto à molécula que utiliza.',
  groupLabelSingular: 'Medicamento à base de',
  groupLabelPlural: 'Medicamentos à base de',
  clinicalNote:
    'O tratamento pode incluir medicamentos quando houver indicação clínica e decisão do profissional responsável.',
  pharmacyNote:
    'Após receber uma prescrição, quando indicada pelo médico, o paciente pode adquirir o medicamento na farmácia de sua preferência ou utilizar uma das opções oferecidas por farmácias parceiras através da plataforma.'
} as const

/**
 * PRD §19.
 * REQUIRES MEDICAL REVIEW.
 */
export const HOME_FOLLOW_UP = {
  title: 'Por que o acompanhamento médico é importante?',
  paragraphs: [
    'Medicamentos utilizados para obesidade, sobrepeso ou diabetes não são adequados para todas as pessoas.',
    'Histórico clínico, outros medicamentos, contraindicações, efeitos adversos e resposta ao tratamento precisam ser avaliados individualmente.',
    'O acompanhamento também permite avaliar a necessidade de manter, modificar ou interromper uma estratégia terapêutica.'
  ]
} as const

export const HOME_FAQ_TITLE = 'Perguntas frequentes'

/**
 * PRD §20 — the 14 homepage questions, verbatim.
 * This single array feeds BOTH the visible accordion and the FAQPage JSON-LD (PRD §54): never add an
 * answer to one side only.
 * REQUIRES MEDICAL REVIEW · REQUIRES LEGAL REVIEW ("A Medleve vende medicamentos?", "A consulta garante receita?").
 */
export const HOME_FAQ: FaqItem[] = [
  {
    question: 'Como funciona a consulta da Medleve?',
    answer:
      'A Medleve conecta o paciente a um médico para uma avaliação online. O profissional analisa informações sobre saúde, peso, medicamentos utilizados, objetivos e outros fatores relevantes antes de orientar os próximos passos.'
  },
  {
    question: 'A consulta é online?',
    answer: 'Sim. A Medleve oferece consultas por mensagem e vídeo.'
  },
  {
    question: 'Quanto tempo demora para falar com um médico por vídeo?',
    answer:
      'A modalidade de vídeo permite atendimento em poucos minutos, de acordo com a disponibilidade dos profissionais.'
  },
  {
    question: 'A consulta garante receita?',
    answer: 'Não. A emissão depende da avaliação médica e da existência de indicação clínica.'
  },
  {
    question: 'É possível receber receita digital?',
    answer: 'Sim. Quando houver indicação clínica, o médico poderá emitir uma receita digital.'
  },
  {
    question: 'A Medleve avalia pacientes interessados em Mounjaro?',
    answer: 'Sim. O médico pode avaliar se tirzepatida ou outra opção terapêutica é adequada ao caso individual.'
  },
  {
    question: 'E Wegovy?',
    answer: 'O médico pode avaliar tratamentos com semaglutida quando houver indicação clínica.'
  },
  {
    question: 'E Ozempic, Poviztra, Semavy e Ozivy?',
    answer:
      'Médicos podem avaliar diferentes opções terapêuticas considerando o quadro clínico do paciente e as indicações de cada medicamento.'
  },
  {
    question: 'Qual a diferença entre semaglutida e tirzepatida?',
    answer:
      'A semaglutida atua no receptor de GLP-1. A tirzepatida atua nos receptores de GIP e GLP-1. São moléculas diferentes.'
  },
  {
    question: 'Mounjaro e Wegovy são iguais?',
    answer: 'Não. Mounjaro utiliza tirzepatida, enquanto Wegovy utiliza semaglutida.'
  },
  {
    question: 'Ozempic e Wegovy são iguais?',
    answer: 'Não. Embora ambos utilizem semaglutida, são medicamentos distintos.'
  },
  {
    question: 'Posso escolher qual medicamento quero usar?',
    answer:
      'O paciente pode discutir suas preferências com o médico, mas a indicação depende da avaliação clínica individual.'
  },
  {
    question: 'A Medleve vende medicamentos?',
    answer:
      'A Medleve atua como plataforma de saúde digital e intermediadora. Após receber uma prescrição, quando indicada pelo médico, o paciente pode comprar o medicamento em uma farmácia de sua escolha ou através de uma farmácia parceira disponível na plataforma.'
  },
  {
    question: 'Preciso fazer acompanhamento?',
    answer:
      'O acompanhamento médico é importante para avaliar resposta, tolerabilidade, efeitos adversos e necessidade de ajustes.'
  }
]

/**
 * PRD §21.
 * REQUIRES MEDICAL REVIEW · REQUIRES LEGAL REVIEW (institutional positioning, PRD §4).
 */
export const HOME_ABOUT = {
  title: 'Sobre a Medleve',
  paragraphs: [
    'A Medleve é uma plataforma brasileira de saúde digital que conecta pacientes a médicos para avaliações e acompanhamento por telemedicina.',
    'A plataforma facilita o acesso a profissionais de saúde para avaliação de obesidade, sobrepeso e tratamentos relacionados, com atendimento por mensagem ou vídeo e prescrição digital quando houver indicação clínica.',
    'A decisão sobre qualquer tratamento é realizada pelo médico responsável de forma independente.'
  ]
} as const

/**
 * Disclaimer of the current landing, without the asterisk that pointed to the old headline.
 * REQUIRES MEDICAL REVIEW.
 */
export const HOME_DISCLAIMER = 'A indicação e prescrição dos tratamentos estão sujeitas à avaliação médica.'

/** Heading for the patient video block of the current landing (it had an empty `<h2>`). */
export const HOME_VIDEO_TESTIMONIES_TITLE = 'Depoimentos em vídeo'

/**
 * Footer texts copied verbatim from the current Medleve footer (`CompanyInfoSectionMedleve`).
 * REQUIRES LEGAL REVIEW — do not edit (PRD §5).
 */
export const HOME_FOOTER_LEGAL = {
  dpo: 'O Encarregado pelo Tratamento de Dados Pessoais (DPO) é o responsável pela proteção dos dados pessoais tratados pela Medleve. Para assuntos relacionados à LGPD ou para o exercício dos direitos previstos no art. 18 da Lei nº 13.709/2018 pelos titulares de dados, o DPO pode ser contatado pelo e-mail dpo@omens.com.br.',
  copyright: '2025 © Medleve. Todos os direitos reservados.',
  responsibility:
    'As informações e serviços oferecidos nesta plataforma são de responsabilidade exclusiva da Medleve (Marca registrada da empresa Omens Serviços Online e Software, CNPJ 38.143.501/0001-08) e dos profissionais de saúde que atuam nela. Os profissionais de saúde são independentes para definir o melhor tratamento para o seu paciente. Recomendamos que qualquer paciente discuta suas condições de saúde e opções de tratamento diretamente com seu médico de confiança.'
} as const
